import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";

import { useSetUserEnabled, useUser } from "@app/api/users";
import { extractErrorMessage } from "@app/api/client";
import { ErrorMessage, Loading } from "@app/components/Feedback";

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const userId = Number(id);

  const { data: user, isLoading, error } = useUser(userId);
  const setEnabled = useSetUserEnabled();

  const onToggle = async (): Promise<void> => {
    if (!user) return;
    const enable = !user.enabled;
    const verb = enable ? "enable" : "disable";
    if (!window.confirm(`Are you sure you want to ${verb} ${user.email}?`)) return;
    try {
      await setEnabled.mutateAsync({ id: user.id, enabled: enable });
      enqueueSnackbar(`User ${verb}d`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err, `Failed to ${verb} user`), { variant: "error" });
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/users")} sx={{ mb: 2 }}>
        Back to users
      </Button>

      {isLoading && <Loading />}
      {error && <ErrorMessage error={error} />}

      {user && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5">{user.email}</Typography>
                  <Typography color="text.secondary">@{user.username}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={user.enabled ? "Enabled" : "Disabled"}
                      color={user.enabled ? "success" : "default"}
                    />
                    <Chip size="small" label={user.verified ? "Verified" : "Unverified"} variant="outlined" />
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  color={user.enabled ? "error" : "success"}
                  onClick={onToggle}
                  disabled={setEnabled.isPending}
                >
                  {user.enabled ? "Disable user" : "Enable user"}
                </Button>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Joined {new Date(user.createdAt).toLocaleString()}
                {user.disabledAt && ` · Disabled ${new Date(user.disabledAt).toLocaleString()}`}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Organizations
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.memberships.map((m) => (
                    <TableRow key={m.organizationId} hover>
                      <TableCell>
                        <Link
                          component="button"
                          onClick={() => navigate(`/organizations/${m.organizationId}`)}
                        >
                          {m.organizationName}
                        </Link>
                      </TableCell>
                      <TableCell>{m.role}</TableCell>
                      <TableCell>{m.status}</TableCell>
                    </TableRow>
                  ))}
                  {user.memberships.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No memberships
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Games created ({user.games.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {user.games.map((g) => (
                    <TableRow key={g.id} hover>
                      <TableCell>{g.name ?? `Game #${g.id}`}</TableCell>
                      <TableCell>
                        <Chip size="small" label={g.status} />
                      </TableCell>
                      <TableCell>{g.organizationName ?? "—"}</TableCell>
                      <TableCell>{new Date(g.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {user.games.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No games
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
